/**
 * EquipmentTypeEditorPage — Equipment Type create + edit form.
 * STORY-1306 AC#3 (Sprint 17 W3).
 *
 * Dual-mode: /new (create) or /:id/edit (edit). Form fields D1-D7 per Sally
 * 08-* ratify-doc. Phase 1 MVP — specsJson editor + units list + documents
 * list deferred to Sprint 18+.
 *
 * D5 Phase 1: heroImageUrl (URL input + preview) + galleryImages (URL list).
 */

import { ApiError } from '@/api/client';
import { createEquipmentTypeSchema, type CreateEquipmentTypeInput } from '@/api/equipment-types';
import { Button, Card, CardContent, Input } from '@/components/ui';
import {
  useCreateEquipmentType,
  useEquipmentType,
  useUpdateEquipmentType,
} from '@/hooks/use-equipment-types';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import { ChipInput, DetailPageTabs } from '@kgc/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EquipmentTypeDocsTab } from './EquipmentTypeDocsTab';
import { EquipmentTypeUnitsTab } from './EquipmentTypeUnitsTab';
import { EquipmentTypeWebshopTab } from './EquipmentTypeWebshopTab';
import { SpecsJsonEditor } from './SpecsJsonEditor';

const CATEGORIES = ['SMALL_ENGINE', 'LAWN_EQUIPMENT', 'POWER_TOOL', 'OTHER'] as const;

interface FormState {
  name: string;
  sku: string;
  manufacturerSku: string;
  alternativeSkusText: string; // comma-separated
  category: 'OTHER' | 'POWER_TOOL' | 'SMALL_ENGINE' | 'LAWN_EQUIPMENT';
  brand: string;
  model: string;
  description: string;
  heroImageUrl: string;
  galleryImagesText: string; // newline-separated
  specsJsonText: string; // canonical JSON text ('' = nincs specs) — SpecsJsonEditor
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  sku: '',
  manufacturerSku: '',
  alternativeSkusText: '',
  category: 'SMALL_ENGINE',
  brand: '',
  model: '',
  description: '',
  heroImageUrl: '',
  galleryImagesText: '',
  specsJsonText: '',
  isActive: true,
};

function trimOrNull(value: string): string | null {
  const t = value.trim();
  return t.length > 0 ? t : null;
}

function splitList(value: string, separator: string | RegExp): string[] {
  return value
    .split(separator)
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

function formToInput(form: FormState): CreateEquipmentTypeInput {
  return {
    name: form.name.trim(),
    sku: form.sku.trim(),
    manufacturerSku: trimOrNull(form.manufacturerSku),
    alternativeSkus: splitList(form.alternativeSkusText, ','),
    category: form.category,
    brand: trimOrNull(form.brand),
    model: trimOrNull(form.model),
    description: trimOrNull(form.description),
    heroImageUrl: trimOrNull(form.heroImageUrl),
    galleryImages: splitList(form.galleryImagesText, /\r?\n/),
    // A handleSubmit specs-guardja garantálja a valid JSON-t (parse nem dobhat itt)
    specsJson:
      form.specsJsonText.trim().length > 0
        ? (JSON.parse(form.specsJsonText) as Record<string, unknown>)
        : null,
    isActive: form.isActive,
  };
}

export function EquipmentTypeEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const { type, isLoading } = useEquipmentType(id ?? null);
  const createMutation = useCreateEquipmentType();
  const updateMutation = useUpdateEquipmentType();

  // Slice B: edit-módban tab-váz (alapadatok / egységek), URL-szinkronnal (?tab=)
  const [activeTab, setActiveTab] = useTabUrlSync('tab', 'data');

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [baseline, setBaseline] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  // CR M6 patch: hero image preview error tracked in React state (no DOM mutation).
  const [heroImageError, setHeroImageError] = useState(false);
  useEffect(() => {
    setHeroImageError(false);
  }, [form.heroImageUrl]);

  // Hydrate form when type loads (edit mode).
  useEffect(() => {
    if (type) {
      const hydrated: FormState = {
        name: type.name,
        sku: type.sku,
        manufacturerSku: type.manufacturerSku ?? '',
        alternativeSkusText: type.alternativeSkus.join(', '),
        category:
          (['OTHER', 'POWER_TOOL', 'SMALL_ENGINE', 'LAWN_EQUIPMENT'] as const).find(
            c => c === type.category
          ) ?? 'OTHER',
        brand: type.brand ?? '',
        model: type.model ?? '',
        description: type.description ?? '',
        heroImageUrl: type.heroImageUrl ?? '',
        galleryImagesText: type.galleryImages.join('\n'),
        specsJsonText: type.specsJson ? JSON.stringify(type.specsJson, null, 2) : '',
        isActive: type.isActive,
      };
      setForm(hydrated);
      setBaseline(hydrated);
    }
  }, [type]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  // CR M15 patch: dirty-state detection (Cancel button confirms before discard).
  const isDirty = useMemo(() => {
    return (Object.keys(form) as Array<keyof FormState>).some(k => form[k] !== baseline[k]);
  }, [form, baseline]);

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(t('equipment.types.editor.cancelConfirm'));
      if (!confirmed) return;
    }
    navigate('/admin/equipment-types');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.name.trim().length === 0) {
      setError(t('equipment.types.editor.errors.nameRequired'));
      return;
    }
    if (form.sku.trim().length === 0) {
      setError(t('equipment.types.editor.errors.skuRequired'));
      return;
    }

    // Slice A (STORY-1306 Phase 2): specs JSON-guard a payload-építés előtt
    if (form.specsJsonText.trim().length > 0) {
      try {
        const parsedSpecs: unknown = JSON.parse(form.specsJsonText);
        if (parsedSpecs === null || typeof parsedSpecs !== 'object' || Array.isArray(parsedSpecs)) {
          setError(t('equipment.types.editor.errors.specsInvalidJson'));
          return;
        }
      } catch {
        setError(t('equipment.types.editor.errors.specsInvalidJson'));
        return;
      }
    }

    const payload = formToInput(form);

    // CR M9 patch: client-side zod parse before request — gives inline field
    // feedback for invalid heroImageUrl / galleryImages URLs without round-tripping.
    const parsed = createEquipmentTypeSchema.safeParse(payload);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      const path = firstIssue?.path.join('.') ?? '';
      const msg = firstIssue?.message ?? '';
      setError(
        path
          ? `${t('equipment.types.editor.errors.validationFailed')} (${path}: ${msg})`
          : t('equipment.types.editor.errors.validationFailed')
      );
      return;
    }

    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, input: payload });
        toast({ title: t('equipment.types.editor.toast.updated') });
      } else {
        await createMutation.mutateAsync(payload);
        toast({ title: t('equipment.types.editor.toast.created') });
      }
      navigate('/admin/equipment-types');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'EQUIPMENT_DUPLICATE_SKU') {
        setError(t('equipment.types.editor.errors.duplicateSku'));
      } else if (code === 'EQUIPMENT_VALIDATION_FAILED') {
        setError(t('equipment.types.editor.errors.validationFailed'));
      } else if (code === 'EQUIPMENT_NOT_FOUND') {
        setError(t('equipment.types.editor.errors.notFound'));
      } else if (code === 'EQUIPMENT_BUSINESS_RULE_FAIL') {
        // CR M5 patch: explicit 422 business-rule branch (was falling through to network).
        setError(t('equipment.types.editor.errors.businessRuleFail'));
      } else if (code === 'EQUIPMENT_FK_VIOLATION') {
        setError(t('equipment.types.editor.errors.fkViolation'));
      } else {
        setError(t('equipment.types.editor.errors.network'));
      }
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-sm text-muted-foreground">{t('equipment.types.editor.loading')}</p>
      </div>
    );
  }

  if (isEdit && !isLoading && !type) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        <p className="text-sm text-red-700 dark:text-red-300">
          {t('equipment.types.editor.errors.notFound')}
        </p>
        <Button variant="outline" onClick={() => navigate('/admin/equipment-types')}>
          {t('equipment.types.editor.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEdit ? t('equipment.types.editor.title.edit') : t('equipment.types.editor.title.new')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t('equipment.types.editor.subtitle')}</p>
      </div>

      {(() => {
        const formNode = (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.name')}{' '}
                      <span className="text-red-600">*</span>
                    </label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder={t('equipment.types.editor.field.namePlaceholder')}
                      data-testid="equipment-type-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.sku')}{' '}
                      <span className="text-red-600">*</span>
                    </label>
                    <Input
                      value={form.sku}
                      onChange={e => setForm({ ...form, sku: e.target.value })}
                      placeholder={t('equipment.types.editor.field.skuPlaceholder')}
                      data-testid="equipment-type-sku"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.manufacturerSku')}
                    </label>
                    <Input
                      value={form.manufacturerSku}
                      onChange={e => setForm({ ...form, manufacturerSku: e.target.value })}
                      data-testid="equipment-type-manufacturer-sku"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.category')}{' '}
                      <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={form.category}
                      onChange={e =>
                        setForm({ ...form, category: e.target.value as FormState['category'] })
                      }
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
                      data-testid="equipment-type-category"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c} value={c}>
                          {t(`equipment.types.categories.${c}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.brand')}
                    </label>
                    <Input
                      value={form.brand}
                      onChange={e => setForm({ ...form, brand: e.target.value })}
                      data-testid="equipment-type-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('equipment.types.editor.field.model')}
                    </label>
                    <Input
                      value={form.model}
                      onChange={e => setForm({ ...form, model: e.target.value })}
                      data-testid="equipment-type-model"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('equipment.types.editor.field.alternativeSkus')}
                  </label>
                  {/* #12 (17-finalized): ChipInput (STORY-1104) — a STORY-1306 D2
                      eredeti chip-pattern előírása. A kanonikus state marad a vesszős
                      string (string-only dirty-compare), split/join adapterrel. */}
                  <ChipInput
                    values={splitList(form.alternativeSkusText, ',')}
                    onChange={values =>
                      setForm(prev => ({ ...prev, alternativeSkusText: values.join(', ') }))
                    }
                    placeholder={t('equipment.types.editor.field.alternativeSkusPlaceholder')}
                    maxChips={50}
                    validator={v =>
                      v.length <= 100
                        ? { ok: true }
                        : {
                            ok: false,
                            error: t('equipment.types.editor.field.alternativeSkusTooLong'),
                          }
                    }
                    labels={{
                      create: (v: string) =>
                        t('equipment.types.editor.field.alternativeSkusCreate', { value: v }),
                      duplicate: t('equipment.types.editor.field.alternativeSkusDuplicate'),
                      remove: t('common.delete'),
                    }}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {t('equipment.types.editor.field.alternativeSkusHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('equipment.types.editor.field.description')}
                  </label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                    data-testid="equipment-type-description"
                  />
                </div>
              </CardContent>
            </Card>

            {/* D5 Phase 1: Images */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('equipment.types.editor.images.title')}
                </h2>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('equipment.types.editor.field.heroImageUrl')}
                  </label>
                  <Input
                    type="url"
                    value={form.heroImageUrl}
                    onChange={e => setForm({ ...form, heroImageUrl: e.target.value })}
                    placeholder="https://..."
                    data-testid="equipment-type-hero-image"
                  />
                  {form.heroImageUrl.trim().length > 0 && !heroImageError && (
                    <div className="mt-2 aspect-video rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 max-w-sm">
                      <img
                        src={form.heroImageUrl}
                        alt="hero preview"
                        className="w-full h-full object-cover"
                        /* CR M6 patch: React state instead of DOM mutation — preview resets on URL change. */
                        onError={() => setHeroImageError(true)}
                      />
                    </div>
                  )}
                  {form.heroImageUrl.trim().length > 0 && heroImageError && (
                    <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                      {t('equipment.types.editor.images.previewError')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('equipment.types.editor.field.galleryImages')}
                  </label>
                  <textarea
                    value={form.galleryImagesText}
                    onChange={e => setForm({ ...form, galleryImagesText: e.target.value })}
                    rows={4}
                    placeholder={t('equipment.types.editor.field.galleryImagesPlaceholder')}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                    data-testid="equipment-type-gallery-images"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {t('equipment.types.editor.field.galleryImagesHint')}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* STORY-1306 Phase 2 slice A: műszaki specifikációk (specsJson) */}
            <Card>
              <CardContent className="p-5 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('equipment.types.editor.specs.title')}
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('equipment.types.editor.specs.hint')}
                  </p>
                </div>
                <SpecsJsonEditor
                  value={form.specsJsonText}
                  onChange={next => setForm(prev => ({ ...prev, specsJsonText: next }))}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={e => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="equipment-type-is-active"
                  />
                  <span className="text-sm font-semibold">
                    {t('equipment.types.editor.field.isActive')}
                  </span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-6">
                  {t('equipment.types.editor.field.isActiveHint')}
                </p>
              </CardContent>
            </Card>

            {error && (
              <Card className="border-red-200 dark:border-red-800">
                <CardContent
                  className="p-3 text-sm text-red-700 dark:text-red-300"
                  data-testid="equipment-type-error"
                >
                  {error}
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isPending}
                data-testid="equipment-type-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isPending} data-testid="equipment-type-submit">
                {isPending
                  ? t('equipment.types.editor.submitting')
                  : isEdit
                    ? t('equipment.types.editor.save')
                    : t('equipment.types.editor.create')}
              </Button>
            </div>
          </form>
        );
        // Slice B (STORY-1306 P2): edit-módban DetailPageTabs — alapadatok + egységek.
        // Új típusnál nincs tab (egység csak létező típushoz tartozhat).
        if (!isEdit || !id) return formNode;
        return (
          <DetailPageTabs
            idPrefix="equipment-type-editor"
            ariaLabel={t('equipment.types.editor.tabsLabel')}
            value={activeTab}
            onValueChange={setActiveTab}
            tabs={[
              { key: 'data', label: t('equipment.types.editor.tabs.data'), content: formNode },
              {
                key: 'webshop',
                label: t('equipment.types.editor.tabs.webshop'),
                content: <EquipmentTypeWebshopTab typeId={id} />,
              },
              {
                key: 'units',
                label: t('equipment.types.editor.tabs.units'),
                content: <EquipmentTypeUnitsTab typeId={id} />,
              },
              {
                key: 'docs',
                label: t('equipment.types.editor.tabs.docs'),
                content: <EquipmentTypeDocsTab typeId={id} />,
              },
            ]}
          />
        );
      })()}
    </div>
  );
}
